import React from 'react';
import moment from 'moment';
import { get, isArray, isEmpty, last } from 'lodash';
import { Grid } from '@material-ui/core';
import ReadMore from 'components/common/ReadMore';
import { capitaliseFirstLetter } from './index';
import {
  HR_24_FORMAT,
  HR_24_AM_PM,
  DATE_VIEW_FORMAT,
  DATE_FORMAT,
  SCHEDULED,
  ENROLL_TYPE,
} from './constants';
import Text from '../components/common/Text';

// Returns the highest value of `field` among the enrichmentLevelConfigs
// entries whose fkLevel is in selectedLevelIds, since those per-level configs
// are the source of truth when a programme has no single top-level value.
export const getMaxLevelConfigValue = (
  levelConfigs = [],
  selectedLevelIds = [],
  field
) =>
  Math.max(
    0,
    ...levelConfigs
      .filter(levelConfig =>
        (selectedLevelIds || []).includes(get(levelConfig, 'fkLevel'))
      )
      .map(levelConfig => get(levelConfig, field, 0))
  );

// For auto-enrol programmes, session duration lives per-level in
// enrichmentLevelConfigs rather than a single top-level value, so it must be
// derived from the longest session among the levels actually in use.
export const getEnrichmentSessionDuration = (
  { enrollType, durationInMinute, enrichmentLevelConfigs = [] } = {},
  selectedLevelIds = []
) => {
  if (enrollType !== ENROLL_TYPE.AUTO) {
    return durationInMinute || 0;
  }
  return getMaxLevelConfigValue(
    enrichmentLevelConfigs,
    selectedLevelIds,
    'sessionDuration'
  );
};

export const getClassSchedule = classDetail => {
  const getDateTimeData = data => {
    const day = capitaliseFirstLetter(get(data, 'day', '-'));
    const startTime = get(data, 'startTime')
      ? moment(get(data, 'startTime'), HR_24_FORMAT).format(HR_24_AM_PM)
      : '';
    const endTime = get(data, 'endTime')
      ? moment(get(data, 'endTime'), HR_24_FORMAT).format(HR_24_AM_PM)
      : '';
    const effectiveFrom = get(data, 'effectiveFrom')
      ? moment(get(data, 'effectiveFrom')).format(DATE_VIEW_FORMAT)
      : '-';
    const effectiveTo = get(data, 'effectiveTo')
      ? moment(get(data, 'effectiveTo')).format(DATE_VIEW_FORMAT)
      : '';
    return {
      day,
      startTime,
      endTime,
      effectiveFrom,
      effectiveTo,
    };
  };

  if (get(classDetail, 'currentSchedule.effectiveFrom')) {
    return getDateTimeData(get(classDetail, 'currentSchedule'));
  }

  if (get(classDetail, 'futureSchedules.data.length')) {
    return getDateTimeData(get(classDetail, 'futureSchedules.data[0]'));
  }

  if (get(classDetail, 'pastSchedules.data.length')) {
    return getDateTimeData(get(classDetail, 'pastSchedules.data[0]'));
  }

  return getDateTimeData(get(classDetail, 'enrichmentClassSchedules.data[0]'));
};

export const getFeePerMonth = (
  currentFee,
  GST,
  sessionPerMonth = 4,
  type = 'float'
) => {
  let feePerMonth = currentFee * sessionPerMonth;
  if (GST) {
    feePerMonth = (feePerMonth * GST) / 100 + feePerMonth;
  }
  feePerMonth = Math.round(feePerMonth * 100) / 100;
  switch (type) {
    case 'int':
      feePerMonth = parseInt(feePerMonth);
      break;
    case 'string':
      feePerMonth = `$${feePerMonth.toFixed(2)}`;
      break;
    default:
      feePerMonth = feePerMonth.toFixed(2);
  }

  return feePerMonth;
};

export const effectiveDateFrag = (
  currentEffectiveDate,
  futureEffectiveDate = [],
  today = moment()
) => {
  let effectiveFrom = '-';
  let effectiveTo = '-';
  let day = '-';
  let startTime = '-';
  let endTime = '-';
  const formatDate = date => moment(date).format(DATE_VIEW_FORMAT);
  const firstFutureSchedule = get(futureEffectiveDate, '[0]');
  if (
    currentEffectiveDate &&
    moment(currentEffectiveDate.effectiveTo).diff(today.endOf('month')) > 0
  ) {
    effectiveFrom = formatDate(currentEffectiveDate.effectiveFrom);
    effectiveTo = formatDate(currentEffectiveDate.effectiveTo);
    day = capitaliseFirstLetter(currentEffectiveDate.day);
    startTime = moment(
      get(currentEffectiveDate, 'startTime'),
      HR_24_FORMAT
    ).format(HR_24_AM_PM);
    endTime = moment(get(currentEffectiveDate, 'endTime'), HR_24_FORMAT).format(
      HR_24_AM_PM
    );
  } else if (!isEmpty(futureEffectiveDate) && firstFutureSchedule) {
    effectiveFrom = formatDate(firstFutureSchedule.effectiveFrom);
    effectiveTo = formatDate(firstFutureSchedule.effectiveTo);
    day = capitaliseFirstLetter(firstFutureSchedule.day);
    startTime = moment(
      get(firstFutureSchedule, 'startTime'),
      HR_24_FORMAT
    ).format(HR_24_AM_PM);
    endTime = moment(get(firstFutureSchedule, 'endTime'), HR_24_FORMAT).format(
      HR_24_AM_PM
    );
  }
  return {
    effectiveFrom,
    effectiveTo,
    day,
    startTime,
    endTime,
  };
};

export const getReschedulingMinDate = scheduledInfo => {
  if (
    moment(get(scheduledInfo, 'originalScheduleDate'), DATE_FORMAT).isAfter(
      moment(),
      'month'
    )
  ) {
    return new Date(
      moment(get(scheduledInfo, 'originalScheduleDate'), DATE_FORMAT).startOf(
        'month'
      )
    );
  }

  if (
    moment(get(scheduledInfo, 'scheduleDate'), DATE_FORMAT).isSame(
      moment(),
      'month'
    ) ||
    get(scheduledInfo, 'originalScheduleDate')
  ) {
    return new Date();
  }

  return new Date(
    moment(get(scheduledInfo, 'scheduleDate'), DATE_FORMAT).startOf('month')
  );
};

export const twoLineDisplay = (
  lineOne,
  lineTwo,
  bold = true,
  color = 'default'
) => (
  <Grid container spacing={1}>
    <Grid item xs={12}>
      <Text color={color} body>
        {lineOne}
      </Text>
    </Grid>
    <Grid item xs={12}>
      <Text color={color} label bold={bold}>
        {lineTwo}
      </Text>
    </Grid>
  </Grid>
);

export const renderProgrammeDesc = (programDesc, isXs) => {
  const charCount = programDesc.length;

  if ((isXs && charCount <= 150) || (!isXs && charCount <= 430)) {
    return programDesc;
  }

  if (isXs && charCount > 150) {
    return <ReadMore text={programDesc} limit={150} />;
  }

  return <ReadMore text={programDesc} limit={430} />;
};

export const getCurrentAndUpcomingEnrolmentNumber = (
  schedule,
  classDetail,
  t
) => {
  const currentMonthEnrolment = {
    month: moment().format('MMM'),
    enrolment: 'N/A',
  };

  const upcomingMonthEnrolment = {
    month: moment().add(1, 'month').format('MMM'),
    enrolment: 'N/A',
  };

  const futureDate =
    get(classDetail, 'futureSchedules.data').length > 0
      ? moment(
          last(get(classDetail, 'futureSchedules.data')).effectiveTo
        ).format(DATE_VIEW_FORMAT)
      : moment(get(schedule, 'effectiveTo'));

  if (
    moment().isBetween(
      moment(get(schedule, 'effectiveFrom')),
      moment(get(schedule, 'effectiveTo'))
    )
  ) {
    currentMonthEnrolment.enrolment = get(classDetail, 'currentSessionsActive')
      ? get(classDetail, 'currentActiveEnrolments')
      : t('enrichment_programme.suspended');
  }

  if (
    moment()
      .add(1, 'month')
      .isBetween(moment(get(schedule, 'effectiveFrom')), futureDate)
  ) {
    upcomingMonthEnrolment.enrolment = get(
      classDetail,
      'upcomingSessionsActive'
    )
      ? get(classDetail, 'upcomingActiveEnrolments')
      : t('enrichment_programme.suspended');
  }

  if (
    get(classDetail, 'displayStatus') === SCHEDULED &&
    get(classDetail, 'futureSchedules.data').length > 0
  ) {
    currentMonthEnrolment.month = moment(
      get(classDetail, 'futureSchedules.data[0].effectiveFrom')
    ).format('MMM');

    currentMonthEnrolment.enrolment = get(
      classDetail,
      'currentActiveEnrolments'
    );

    upcomingMonthEnrolment.month = moment(
      get(classDetail, 'futureSchedules.data[0].effectiveFrom')
    )
      .add(1, 'month')
      .format('MMM');

    upcomingMonthEnrolment.enrolment = get(
      classDetail,
      'upcomingActiveEnrolments'
    );
  }

  return { currentMonthEnrolment, upcomingMonthEnrolment };
};

export const getFilterSource = (
  source,
  fieldMaps = ['ID', 'ID', 'label'],
  isClass = false
) =>
  isArray(source)
    ? source.map(item => ({
        value: item[fieldMaps[0]],
        label: item[fieldMaps[1]],
        description: isClass
          ? `${item[fieldMaps[2]]} (${get(item, 'centre.label', '')})`
          : item[fieldMaps[2]],
      }))
    : [];

export const hideLoginByLinkpass = () => {
  const isEP = process.env.REACT_APP_ENRICHMENT_PLATFORM === 'true';
  const schoolId = process.env.REACT_APP_SCHOOL_ID;

  // Hide login by linkpass for LSH EP
  return isEP && schoolId === '2';
};

export const getEnrichmentLevelConfig = (data = [], levelId) => {
  return data.find(item => item.fkLevel === levelId);
};
